(() => {
  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[character]));

  function initializeHeader(header) {
    if (!header || header.dataset.headerInitialized === 'true') return;
    header.dataset.headerInitialized = 'true';

    /* --- DOM Elements --- */
    const burger = header.querySelector('.header-burger');
    const sidebar = header.querySelector('.mobile-sidebar');
    const overlay = header.querySelector('.mobile-sidebar-overlay');
    const sidebarClose = header.querySelector('.mobile-sidebar__close');
    const mobileNavToggles = header.querySelectorAll('.mobile-nav__toggle');

    const mobileSearchTrigger = header.querySelector('.header-search-trigger--mobile');
    const desktopSearchTrigger = header.querySelector('.header-search-trigger');

    const searchPanel = header.querySelector('.header-search-drawer') || header.querySelector('.header-search-responsive');
    const panelInput = searchPanel?.querySelector('.header-search-modal__input');
    const searchForm = searchPanel?.querySelector('.header-search-modal__form-wrap');
    const closeButtons = searchPanel?.querySelectorAll('[data-search-close]') || [];
    const results = searchPanel?.querySelector('[data-search-results]');
    const viewAll = searchPanel?.querySelector('[data-search-view-all]');
    const searchTags = searchPanel?.querySelectorAll('.search-tag') || [];

    const predictiveUrl = header.dataset.predictiveSearchUrl || '/search/suggest';
    const searchUrl = header.dataset.searchUrl || '/search';

    let controller;
    let debounceTimer;

    /* --- Mobile Navigation Drawer Logic --- */
    function openSidebar() {
      if (!sidebar || !overlay || !burger) return;
      sidebar.classList.add('is-open');
      overlay.classList.add('is-visible');
      sidebar.setAttribute('aria-hidden', 'false');
      burger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeSidebar() {
      if (!sidebar || !overlay || !burger) return;
      sidebar.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      sidebar.setAttribute('aria-hidden', 'true');
      burger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    // mobileNavToggles.forEach((toggle) => {
    //   toggle.addEventListener('click', function (e) {
    //     e.stopPropagation();
    //     const parentItem = this.closest('.mobile-nav__item');
    //     const isExpanded = parentItem?.classList.contains('is-expanded');

    //     if (isExpanded) {
    //       parentItem.classList.remove('is-expanded');
    //       this.setAttribute('aria-expanded', 'false');
    //     } else {
    //       parentItem.classList.add('is-expanded');
    //       this.setAttribute('aria-expanded', 'true');
    //     }
    //   });
    // });

    /* --- Search Modal Actions --- */
    function triggerOpenSearch(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!searchPanel) return;

      searchPanel.classList.add('is-open');
      searchPanel.setAttribute('aria-hidden', 'false');
      desktopSearchTrigger?.setAttribute('aria-expanded', 'true');
      mobileSearchTrigger?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      setTimeout(() => panelInput?.focus(), 150);

      const query = panelInput?.value || '';
      if (query.trim()) {
        performSearch(query);
      } else {
        searchPanel.classList.remove('has-query');
        if (results) results.innerHTML = '';
        if (viewAll) viewAll.hidden = true;
      }
    }

    function closeSearch() {
      if (!searchPanel) return;
      searchPanel.classList.remove('is-open');
      searchPanel.setAttribute('aria-hidden', 'true');
      desktopSearchTrigger?.setAttribute('aria-expanded', 'false');
      mobileSearchTrigger?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    /* --- Render Helpers --- */
    function getImageUrl(item) {
      const image = item.featured_image || item.image;
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.url || image.src || '';
    }

    function formatRupees(value) {
      if (value === null || value === undefined || value === '') return '';
      let num = Number(value);
      if (num > 10000) num = num / 100;
      return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function renderProductCard(item) {
      const imageUrl = getImageUrl(item);
      const price = item.price;
      const compareAtPrice = item.compare_at_price;

      const compareAtMarkup = compareAtPrice && Number(compareAtPrice) > Number(price)
        ? `<span class="header-search-product__mrp" style="text-decoration:line-through;color:#888;font-size:11px;">${escapeHtml(formatRupees(compareAtPrice))}</span>` : '';
      const priceMarkup = price !== undefined ? `<span class="header-search-product__price">${escapeHtml(formatRupees(price))}</span>` : '';

      const imageMarkup = imageUrl
        ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.title)}" loading="lazy">`
        : `<div class="header-search-product__placeholder" style="padding:20px;color:#888;font-size:11px;">No image</div>`;

      return `
        <a class="header-search-product" href="${escapeHtml(item.url)}">
          <div class="header-search-product__media">${imageMarkup}</div>
          <div class="header-search-product__content">
            <span class="header-search-product__title">${escapeHtml(item.title)}</span>
            <div class="header-search-product__pricing">
              ${compareAtMarkup}
              ${priceMarkup}
            </div>
          </div>
        </a>
      `;
    }

    function renderGroup(title, items) {
      if (!items?.length) return '';
      return `
        <section class="header-search-group" style="width:100%;">
          <div class="header-search-products-grid">${items.map(renderProductCard).join('')}</div>
        </section>
      `;
    }

    /* --- Predictive Search Handler --- */
    async function performSearch(term) {
      const query = term.trim();

      if (!query) {
        searchPanel?.classList.remove('has-query');
        if (results) results.innerHTML = '';
        if (viewAll) viewAll.hidden = true;
        return;
      }

      // Hide Hero & Suggestions when query exists
      searchPanel?.classList.add('has-query');

      if (!results) return;

      controller?.abort();
      controller = new AbortController();

      results.setAttribute('aria-busy', 'true');
      results.innerHTML = '<div class="header-search-results__status" style="padding:15px;color:#888;text-align:center;">Searching sarees...</div>';

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '4',
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const response = await fetch(`${predictiveUrl}.json?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Search request failed.');

        const data = await response.json();
        const predictive = data.resources?.results || {};

        const html = renderGroup('Products', predictive.products);

        results.innerHTML = html || '<div class="header-search-results__empty" style="padding:20px;color:#666;text-align:center;">No sarees found matching "' + escapeHtml(query) + '".</div>';
        if (viewAll) {
          viewAll.href = `${searchUrl}?q=${encodeURIComponent(query)}`;
          viewAll.hidden = false;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          results.innerHTML = '<div class="header-search-results__empty" style="padding:20px;color:#666;text-align:center;">Could not load results.</div>';
          if (viewAll) viewAll.hidden = true;
        }
      } finally {
        results.setAttribute('aria-busy', 'false');
      }
    }

    function queueSearch(value) {
      clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => performSearch(value), 250);
    }

    /* --- Event Listeners --- */
    burger?.addEventListener('click', openSidebar);
    sidebarClose?.addEventListener('click', closeSidebar);
    overlay?.addEventListener('click', closeSidebar);

    mobileSearchTrigger?.addEventListener('click', triggerOpenSearch);
    desktopSearchTrigger?.addEventListener('click', triggerOpenSearch);

    closeButtons.forEach((button) => button.addEventListener('click', closeSearch));

    // Tag Click Trigger Logic
    searchTags.forEach(tagBtn => {
      tagBtn.addEventListener('click', () => {
        const keyword = tagBtn.getAttribute('data-search-keyword');
        if (panelInput && keyword) {
          panelInput.value = keyword;
          performSearch(keyword);
        }
      });
    });

    panelInput?.addEventListener('input', () => {
      queueSearch(panelInput.value);
    });

    searchForm?.addEventListener('submit', (e) => {
      if (!panelInput?.value.trim()) {
        e.preventDefault();
      }
    });

    document.addEventListener('click', (event) => {
      if (!searchPanel?.classList.contains('is-open')) return;

      const target = event.target;
      const isClickInsideModal = searchPanel.querySelector('.header-search-modal__drawer-panel')?.contains(target);
      const isClickOnDesktopTrigger = desktopSearchTrigger?.contains(target);
      const isClickOnMobileTrigger = mobileSearchTrigger?.contains(target);

      if (!isClickInsideModal && !isClickOnDesktopTrigger && !isClickOnMobileTrigger) {
        closeSearch();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && searchPanel?.classList.contains('is-open')) {
        closeSearch();
      }
    });
  }

  function initializeAllHeaders(container = document) {
    if (container instanceof Element && container.matches('.site-header')) {
      initializeHeader(container);
    }
    const headers = container.querySelectorAll ? container.querySelectorAll('.site-header') : [];
    headers.forEach(initializeHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllHeaders());
  } else {
    initializeAllHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAllHeaders(event.target);
  });
})();